import { DesktopDatePicker } from '@mui/x-date-pickers';
import '@mui/lab';
import { BoxFC, BoxFR } from "components/BoxCustom"
import React, { useCallback, useMemo, useState, useRef } from "react"
import { Box, Button, Fade, IconButton, InputAdornment, Popover, Radio, RadioGroup, TextField } from "@mui/material";
import ExpandCircleDownRoundedIcon from '@mui/icons-material/ExpandCircleDownRounded';
import RemoveCircleRoundedIcon from '@mui/icons-material/RemoveCircleRounded';
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded';
import dayjs from "dayjs";
import { lightBlue } from '@mui/material/colors';
import BackspaceRoundedIcon from '@mui/icons-material/BackspaceRounded';


const AddIcon = ({ onClick, sx, disabled }) => {
  return (
    <IconButton sx={{ m: 0, p: 0, ...sx }} onClick={onClick} disabled={disabled}>
      <AddCircleRoundedIcon color={disabled ? "disabled" : "primary"} />
    </IconButton >
  )
}

const SubtractIcon = ({ onClick, sx, disabled }) => {
  return (
    <IconButton sx={{ m: 0, p: 0, ...sx }} onClick={onClick} disabled={disabled}>
      <RemoveCircleRoundedIcon color={disabled ? "disabled" : "primary"} />
    </IconButton >
  )
}

const NumDteBox = ({ label, numDte, setNumDte, setSelectDate }) => {
  const handleOnChange = useCallback((e) => {
    const num = +e.target.value
    if (num >= 0) {
      setSelectDate(dayjs().format("YYYY-MM-DD 00:00:00"), dayjs().add(num, "day").format("YYYY-MM-DD 23:59:59"))
    } else {
      setSelectDate(dayjs().add(num, "day").format("YYYY-MM-DD 00:00:00"), dayjs().format("YYYY-MM-DD 23:59:59"))
    }
    setNumDte(num)
  }, [setNumDte, setSelectDate])

  return (
    <TextField fullWidth size="small" label={label} type="number" value={numDte}
      onChange={handleOnChange}
      inputProps={{ style: { textAlign: "center" } }}
      InputProps={{
        startAdornment: <InputAdornment position="start">จากวันนี้</InputAdornment>,
        endAdornment: <InputAdornment position="end">วัน</InputAdornment>,
      }} />
  )
}

const WeekBox = ({ label, numWeek, setNumWeek, setSelectDate }) => {
  const handleChange = useCallback((e) => {
    const num = +e.target.value
    if (num >= 0) {
      setSelectDate(dayjs().startOf("isoWeek").format("YYYY-MM-DD 00:00:00"), dayjs().add(num, "isoWeek").endOf("isoWeek").format("YYYY-MM-DD 23:59:59"))
    } else {
      setSelectDate(dayjs().add(num, "isoWeek").startOf("isoWeek").format("YYYY-MM-DD 00:00:00"), dayjs().endOf("isoWeek").format("YYYY-MM-DD 23:59:59"))
    }
    setNumWeek(num)
  }, [setNumWeek, setSelectDate])

  return (
    <TextField fullWidth size="small" label={label} type="number" value={numWeek || ""}
      onChange={handleChange}
      inputProps={{ style: { textAlign: "center" } }}
      InputProps={{
        startAdornment: <InputAdornment position="start">สัปดาห์นี้</InputAdornment>,
      }} />
  )
}


//----------------------------------------------------------------------Main Component---------------------------------------------------

const DateTimeSelect = ({ label, start, end, setSelectDate, sx, getData, initRadioValue }) => {

  const inputDateEl = useRef(null);
  const [numDte, setNumDte] = useState(1)
  const [numWeek, setNumWeek] = useState(0);
  const [radioValue, setRadioValue] = useState(initRadioValue || "DATE");//DATE, NDAY, WEEK, MONTH, RANGE
  const [anchorEl, setAnchorEl] = useState(null);

  const open = Boolean(anchorEl);

  const displayTextRange = useMemo(() => {
    if (!start || !end) return ""

    const diff = dayjs(end).diff(dayjs(start), 'day')
    return `${dayjs(start).format("DD/MM/YYYY")} + ${diff} วัน`
  }, [start, end])

  const handleDesktopDatePickerChange = useCallback((newValue) => {
    if (newValue) {
      setSelectDate(newValue.format("YYYY-MM-DD 00:00:00"), newValue.format("YYYY-MM-DD 23:59:59"));
    } else {
      setSelectDate(null, null);
    }
  }, [setSelectDate])

  const handleDesktopDatePickerMonthChange = useCallback((newValue) => {
    if (newValue) {
      setSelectDate(newValue.format("YYYY-MM-01 00:00:00"), newValue.endOf("month").format("YYYY-MM-DD 23:59:59"));
    } else {
      setSelectDate(null, null);
    }
  }, [setSelectDate])
  const handleDesktopDatePickerKeyDown = useCallback((e) => {
    if (start && end) {
      if (e.key === "ArrowUp" || e.key === "ArrowDown") {
        e.preventDefault();
        const num = e.key === "ArrowUp" ? 1 : -1
        const st = dayjs(start).add(num, "day")
        const en = dayjs(end).add(num, "day")
        setSelectDate(st.format("YYYY-MM-DD 00:00:00"), en.format("YYYY-MM-DD 23:59:59"));
      } else if (e.key === "Enter") {
       
        if (getData) {
          getData()
        }
      }
    }
  }, [getData, start, end, setSelectDate])

  const handleRangeChange = useCallback((newvalue, pos) => {
    if (!newvalue) {
      setSelectDate(null, null);
      return
    }
    if (pos === "start") {
      setSelectDate(newvalue.format("YYYY-MM-DD 00:00:00"), end)
    } else if (pos === "end") {
      setSelectDate(start, newvalue.format("YYYY-MM-DD 23:59:59"))
    }
  }, [setSelectDate, start, end])

  const datePickerProp = useCallback((label, pos) => ({
    label: label,
    InputAdornmentProps: { style: { marginLeft: 0 } },
    inputFormat: "DD/MM/YYYY",
    mask: "__/__/____",
    onChange: (newValue) => handleRangeChange(newValue, pos),
    renderInput: (params) => <TextField sx={{ width: 145 }} size="small" {...params} />
  }), [handleRangeChange])

  const handleDesktopDatePickerKeyDownMonth = useCallback((e) => {
    if (start && end) {
      if (e.key === "ArrowUp" || e.key === "ArrowDown") {
        e.preventDefault();
        const num = e.key === "ArrowUp" ? 1 : -1
        const st = dayjs(start).add(num, "month")
        const en = st.endOf("month")
        setSelectDate(st.format("YYYY-MM-DD 00:00:00"), en.format("YYYY-MM-DD 23:59:59"));
      } else if (e.key === "Enter") {
       
        if (getData) {
          getData()
        }
      }
    }
  }, [getData, start, end, setSelectDate])

  const displayDesktopDatePickerProp = useMemo(() => ({
    label: label,
    inputFormat: "DD/MM/YYYY",
    mask: "__/__/____",
    value: start,
    InputAdornmentProps: { style: { marginLeft: 0 } },
    onChange: handleDesktopDatePickerChange,
    renderInput: (params) => (
      <TextField sx={{ flexGrow: 1 }} size="small" {...params} onKeyDown={handleDesktopDatePickerKeyDown} />
    ),
  }), [label, start, handleDesktopDatePickerChange, handleDesktopDatePickerKeyDown])

  const displayDesktopDatePickerMonthProp = useMemo(() => ({
    label: label,
    views: ["year", "month"],
    disableMaskedInput: true,
    openTo: 'month',
    inputFormat: "MMM YYYY",
    value: start,
    InputAdornmentProps: { style: { marginLeft: 0 } },
    onChange: handleDesktopDatePickerMonthChange,
    renderInput: (params) => (
      <TextField sx={{ flexGrow: 1 }} size="small" {...params} onKeyDown={handleDesktopDatePickerKeyDownMonth} />
    ),
  }), [label, start, handleDesktopDatePickerMonthChange, handleDesktopDatePickerKeyDownMonth])

  const displayDate = useMemo(() => {
    if (radioValue === "DATE") {
      return <DesktopDatePicker  {...displayDesktopDatePickerProp} clearable={true} />
    } else if (radioValue === "NDAY") {
      return <NumDteBox label={label} numDte={numDte} setNumDte={setNumDte} setSelectDate={setSelectDate} />
    } else if (radioValue === "WEEK") {
      return <WeekBox label={label} numWeek={numWeek} setNumWeek={setNumWeek} setSelectDate={setSelectDate} />
    } else if (radioValue === "MONTH") {
      return <DesktopDatePicker {...displayDesktopDatePickerMonthProp} clearable={true} />
    } else if (radioValue === "RANGE") {
      return <TextField value={displayTextRange} label={label} size="small" onKeyDown={handleDesktopDatePickerKeyDown} />
    }
  }, [radioValue, numDte, displayDesktopDatePickerProp, label, setSelectDate, numWeek, setNumWeek
    , displayDesktopDatePickerMonthProp, displayTextRange, handleDesktopDatePickerKeyDown])

  const handleAddDate = useCallback((num) => {
    if (!start || !end) {
      if (radioValue === "DATE") {
        setSelectDate(dayjs().format("YYYY-MM-DD 00:00:00"), dayjs().format("YYYY-MM-DD 23:59:59"))
      } else if (radioValue === "NDAY") {
        setSelectDate(dayjs().format("YYYY-MM-DD 00:00:00"), dayjs().add(numDte, "day").format("YYYY-MM-DD 23:59:59"));
      } else if (radioValue === "WEEK") {
        setSelectDate(dayjs().startOf("isoWeek").format("YYYY-MM-DD 00:00:00"), dayjs().endOf("isoWeek").format("YYYY-MM-DD 23:59:59"));
      } else if (radioValue === "MONTH") {
        setSelectDate(dayjs().format("YYYY-MM-01 00:00:00"), dayjs().endOf("month").format("YYYY-MM-DD 23:59:59"));
      } else if (radioValue === "RANGE") {
        setSelectDate(dayjs().format("YYYY-MM-DD 00:00:00"), dayjs().format("YYYY-MM-DD 23:59:59"));
      }
      return
    }
    if (radioValue === "DATE") {
      const newDate = dayjs(start).add(num, "day");
      setSelectDate(newDate.format("YYYY-MM-DD 00:00:00"), newDate.format("YYYY-MM-DD 23:59:59"));
    } else if (radioValue === "NDAY") {
      const newNumDte = numDte + num;
      if (newNumDte >= 0) {
        setSelectDate(dayjs().format("YYYY-MM-DD 00:00:00"), dayjs().add(newNumDte, "day").format("YYYY-MM-DD 23:59:59"));
        setNumDte(newNumDte);
      } else {
        setSelectDate(dayjs().add(newNumDte, "day").format("YYYY-MM-DD 00:00:00"), dayjs().format("YYYY-MM-DD 23:59:59"));
        setNumDte(newNumDte);
      }
    } else if (radioValue === "WEEK") {
      const newNumWeek = numWeek + num;
      if (newNumWeek === 0) {
        setSelectDate(dayjs().startOf("isoWeek").format("YYYY-MM-DD 00:00:00"), dayjs().endOf("isoWeek").format("YYYY-MM-DD 23:59:59"));
        setNumWeek(0);
      } else {
        const st = dayjs().add(newNumWeek, "week").startOf("isoWeek");
        setSelectDate(st.format("YYYY-MM-DD 00:00:00"), st.endOf("isoWeek").format("YYYY-MM-DD 23:59:59"));
        setNumWeek(newNumWeek);
      }
    } else if (radioValue === "MONTH") {
      const newMonth = dayjs(start).add(num, "month");
      setSelectDate(newMonth.format("YYYY-MM-01 00:00:00"), newMonth.endOf("month").format("YYYY-MM-DD 23:59:59"));
    }
  }, [radioValue, numDte, start, setSelectDate, numWeek, end])

  const handleRadioChange = useCallback((e) => {
    const value = e.target.value;
    setRadioValue(value)
    if (value === "DATE") {
      setSelectDate(dayjs().format("YYYY-MM-DD 00:00:00"), dayjs().format("YYYY-MM-DD 23:59:59"));
    } else if (value === "NDAY") {
      setSelectDate(dayjs().format("YYYY-MM-DD 00:00:00"), dayjs().add(numDte, "day").format("YYYY-MM-DD 23:59:59"));
      setNumDte(1)
    } else if (value === "WEEK") {
      setSelectDate(dayjs().startOf("isoWeek").format("YYYY-MM-DD 00:00:00"), dayjs().endOf("isoWeek").format("YYYY-MM-DD 23:59:59"));
      setNumWeek(0)
    } else if (value === "MONTH") {
      setSelectDate(dayjs().format("YYYY-MM-01 00:00:00"), dayjs().endOf("month").format("YYYY-MM-DD 23:59:59"));
    } 
  }, [numDte, setSelectDate])


  const handleAddNumRange = useCallback((num, pos) => {
    if (!start || !end) {
      setSelectDate(dayjs().format("YYYY-MM-DD 00:00:00"), dayjs().format("YYYY-MM-DD 23:59:59"))
      return
    }
    if (pos === "start") {
      setSelectDate(dayjs(start).add(num, "day").format("YYYY-MM-DD 00:00:00"), end)
    } else {
      setSelectDate(start, dayjs(end).add(num, "day").format("YYYY-MM-DD 23:59:59"));
    }
  }, [setSelectDate, start, end])

  const handleDeleteDate = useCallback(() => {
    setRadioValue("DATE")
    setNumDte(1);
    setNumWeek(0);
    setSelectDate(null, null);
  }, [setSelectDate])
  return (
    <Box>
      <BoxFR ref={inputDateEl} width={200} sx={{ ...sx, gap: 0, px: 0.9 }} position="relative">

        {displayDate}
        <SubtractIcon sx={{ position: "absolute", top: 8, left: -3 }} onClick={() => handleAddDate(-1)} />
        <AddIcon sx={{ position: "absolute", top: 8, right: -3 }} onClick={() => handleAddDate(1)} />
        {!open &&
          <IconButton sx={{ m: 0, p: 0, position: "absolute", top: 26, left: 0, right: 0, marginLeft: "auto", marginRight: "auto" }} onClick={(e) => setAnchorEl(inputDateEl.current)}>
            <ExpandCircleDownRoundedIcon color="primary" />
          </IconButton >
        }
      </BoxFR>
      <Popover
        id="filterPopOver"
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}>
        <Box>
          <Fade in={open}>
            <RadioGroup value={radioValue} onChange={handleRadioChange}>
              <BoxFC sx={{ gap: 0 }}>

                <BoxFR sx={{ gap: 0, p: 1, bgcolor: radioValue === "DATE" && lightBlue[50] }}>
                  <Radio value="DATE" />
                  <SubtractIcon onClick={() => handleAddDate(-1)} disabled={radioValue !== "DATE"} />
                  <Box width={146}><DesktopDatePicker {...displayDesktopDatePickerProp} label="วันที่" /></Box>
                  <AddIcon onClick={() => handleAddDate(1)} disabled={radioValue !== "DATE"} />
                </BoxFR>
                <BoxFR sx={{ gap: 0, mt: 0.5, p: 1, bgcolor: radioValue === "NDAY" && lightBlue[50] }}>
                  <Radio value="NDAY" />
                  <SubtractIcon onClick={() => handleAddDate(-1)} disabled={radioValue !== "NDAY"} />
                  <Box width={146}><NumDteBox label="จำนวนวัน" numDte={numDte} setNumDte={setNumDte} setSelectDate={setSelectDate} /></Box>
                  <AddIcon onClick={() => handleAddDate(1)} disabled={radioValue !== "NDAY"} />
                </BoxFR>

                <BoxFR sx={{ gap: 0, mt: 0.5, p: 1, bgcolor: radioValue === "WEEK" && lightBlue[50] }}>
                  <Radio value="WEEK" />
                  <SubtractIcon onClick={() => handleAddDate(-1)} disabled={radioValue !== "WEEK"} />
                  <Box width={146}><WeekBox label="สัปดาห์" numWeek={numWeek} setNumWeek={setNumWeek} setSelectDate={setSelectDate} /></Box>
                  <AddIcon onClick={() => handleAddDate(1)} disabled={radioValue !== "WEEK"} />
                </BoxFR>

                <BoxFR sx={{ gap: 0, p: 1, bgcolor: radioValue === "MONTH" && lightBlue[50] }}>
                  <Radio value="MONTH" />
                  <SubtractIcon onClick={() => handleAddDate(-1)} disabled={radioValue !== "MONTH"} />
                  <Box width={146}><DesktopDatePicker {...displayDesktopDatePickerMonthProp} label="เดือน" /></Box>
                  <AddIcon onClick={() => handleAddDate(1)} disabled={radioValue !== "MONTH"} />
                </BoxFR>
                <BoxFR sx={{ gap: 0, mt: 0.5, p: 1, bgcolor: radioValue === "RANGE" && lightBlue[50] }}>
                  <Radio value="RANGE" />
                  <BoxFC sx={{ gap: 1 }}>
                    <BoxFR sx={{ gap: 0 }}>
                      <SubtractIcon onClick={() => handleAddNumRange(-1, "start")} disabled={radioValue !== "RANGE"} />
                      <DesktopDatePicker {...datePickerProp("วันเริ่มต้น", "start")} value={start} />
                      <AddIcon onClick={() => handleAddNumRange(1, "start")} disabled={radioValue !== "RANGE"} />
                    </BoxFR>
                    <BoxFR sx={{ gap: 0 }}>
                      <SubtractIcon onClick={() => handleAddNumRange(-1, "end")} disabled={radioValue !== "RANGE"} />
                      <DesktopDatePicker {...datePickerProp("วันสิ้นสุด", "end")} value={end} />
                      <AddIcon onClick={() => handleAddNumRange(1, "end")} disabled={radioValue !== "RANGE"} />
                    </BoxFR>
                  </BoxFC>
                </BoxFR>
                <BoxFR sx={{ gap: 1, p: 1 }}>
                  <Button variant="contained" color="secondary" fullWidth onClick={handleDeleteDate}>
                    <BackspaceRoundedIcon sx={{ mr: 1 }} />
                    ลบวันที่
                  </Button>
                  <Button variant="contained" color="primary" fullWidth onClick={() => setAnchorEl(null)}>ตกลง</Button>
                </BoxFR>

              </BoxFC>
            </RadioGroup>
          </Fade>
        </Box>
      </Popover>
    </Box>
  )
}

export default React.memo(DateTimeSelect)